﻿import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Spin, Switch, Tag, message } from 'antd';
import { mFetch, MApiError } from '../mApi';
import { MPageTitle } from '../MobileApp';

interface Product {
  id?: number | string;
  slug: string;
  title: string;
  subtitle?: string | null;
  isPublished?: boolean;
}

export default function MProducts() {
  const [items, setItems] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await mFetch<{ items: Product[] }>('/admin/cms/products');
      setItems(r.items || []);
    } catch (e) {
      setErr(e instanceof MApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (p: Product) => {
    setEditing(p);
    form.setFieldsValue({ title: p.title, subtitle: p.subtitle || '', isPublished: !!p.isPublished });
  };

  const save = async () => {
    if (!editing) return;
    const v = await form.validateFields();
    setSaving(true);
    try {
      await mFetch(`/admin/cms/products/${encodeURIComponent(editing.slug)}`, {
        method: 'PUT',
        body: JSON.stringify({ title: v.title, subtitle: v.subtitle || null, isPublished: !!v.isPublished }),
      });
      message.success('已保存');
      setEditing(null);
      load();
    } catch (e) {
      message.error(e instanceof MApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <MPageTitle title="产品页" />
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : err ? (
        <div style={{ padding: 16 }}><Alert type="warning" showIcon title={err} /></div>
      ) : (
        <div style={{ padding: '0 16px' }}>
          {items.length === 0 && <div style={{ color: '#8a8aa3', padding: 20, textAlign: 'center' }}>暂无产品</div>}
          {items.map((p) => (
            <div key={p.slug} data-testid="m-product-item" style={{ background: '#141428', border: '1px solid #26264a', borderRadius: 10, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, flex: 1 }}>{p.title}</span>
                <Tag color={p.isPublished ? 'green' : 'default'}>{p.isPublished ? '已上线' : '未上线'}</Tag>
              </div>
              <div style={{ fontSize: 12, color: '#8a8aa3', marginTop: 4 }}>/{p.slug}</div>
              {p.subtitle ? <div style={{ fontSize: 13, color: '#b5b5c9', marginTop: 4 }}>{p.subtitle}</div> : null}
              <div style={{ marginTop: 8 }}>
                <Button size="small" onClick={() => openEdit(p)} data-testid="m-product-edit">编辑</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal
        open={!!editing}
        title={`编辑产品：${editing?.slug || ''}`}
        okText="保存"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        styles={{ body: { background: '#141428' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="标题" rules={[{ required: true, message: '请输入标题' }]}>
            <Input data-testid="m-product-title" />
          </Form.Item>
          <Form.Item name="subtitle" label="副标题">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="isPublished" label="上线" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
